(() => {
  // app/scripts/utils/storage.js
  function sessionStorage(storageKey) {
    const key = storageKey;
    const sessionStore = window.sessionStorage;
    const storage = {
      get: () => {
        try {
          const data = sessionStore.getItem(key);
          return data ? JSON.parse(data) : null;
        } catch (error) {
          console.error(`Error getting item from sessionStorage: ${error.message}`);
          return null;
        }
      },
      set: (value) => {
        try {
          sessionStore.setItem(key, JSON.stringify(value));
        } catch (error) {
          console.error(`Error setting item in sessionStorage: ${error.message}`);
          throw error;
        }
      },
      clear: () => {
        try {
          sessionStore.removeItem(key);
        } catch (error) {
          console.error(`Error clearing item from sessionStorage: ${error.message}`);
          throw error;
        }
      }
    };
    return storage;
  }

  // app/scripts/utils/toVariantGid.js
  function toVariantGid(rawId) {
    const id = String(rawId);
    return id.startsWith("gid://") ? id : `gid://shopify/ProductVariant/${id}`;
  }

  // app/scripts/utils/toAmount.js
  function toAmount(price) {
    if (typeof price !== "number" && typeof price !== "string")
      return null;
    const raw = typeof price === "string" ? price.trim() : price;
    if (raw === "")
      return null;
    const amount = Number(raw);
    return Number.isFinite(amount) ? amount : null;
  }

  // app/scripts/utils/chunk.js
  function chunk(array, size) {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  // app/scripts/utils/delay.js
  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // app/scripts/utils/livePricing.js
  var REQUEST_TIMEOUT_MS = 10 * 1e3;
  var PRICES_REQUEST_TIMEOUT_MS = 35 * 1e3;
  var BATCH_SIZE = 25;
  var UNPRICED_RETRY_DELAY_MS = 1500;
  function getLivePricingConfig() {
    return window.__livePricingConfig || null;
  }
  function getLpCtx() {
    const value = window.cart_attributes?._lp_ctx;
    return typeof value === "string" ? value : "";
  }
  var LP_CTX_CHANGE_EVENT = "lpCtx:change";
  var LP_CTX_WAIT_MS = 5 * 1e3;
  var resolutionInFlight = null;
  var resolvedLpCtx = null;
  function syncLpCtx(attributes) {
    if (!attributes || typeof attributes !== "object" || !window.cart_attributes)
      return;
    resolvedLpCtx = null;
    const next = typeof attributes._lp_ctx === "string" ? attributes._lp_ctx : "";
    if (next === getLpCtx())
      return;
    window.cart_attributes._lp_ctx = next;
    window.dispatchEvent(new CustomEvent(LP_CTX_CHANGE_EVENT, { detail: { ctx: next } }));
  }
  var latestCartRead = 0;
  async function readCartAttributes() {
    const readId = ++latestCartRead;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, {
        headers: { Accept: "application/json" },
        signal: controller.signal
      });
      if (!response.ok)
        throw new Error(`cart.js responded ${response.status}`);
      const cart = await response.json();
      if (readId === latestCartRead)
        syncLpCtx(cart?.attributes);
      return cart?.attributes || {};
    } finally {
      clearTimeout(timer);
    }
  }
  function watchLpCtx() {
    const api = window.livePricing;
    if (!api || api.invalidateDisplay?.themeWatchesLpCtx)
      return;
    const appHandler = api.invalidateDisplay;
    const handler = function(...args) {
      if (typeof appHandler === "function")
        appHandler.apply(this, args);
      readCartAttributes().catch(() => {
      });
    };
    handler.themeWatchesLpCtx = true;
    api.invalidateDisplay = handler;
  }
  function waitForLpCtx() {
    if (getLpCtx())
      return Promise.resolve(getLpCtx());
    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        window.removeEventListener(LP_CTX_CHANGE_EVENT, onChange);
        resolve(getLpCtx());
      };
      const onChange = () => getLpCtx() && done();
      const timer = setTimeout(done, LP_CTX_WAIT_MS);
      window.addEventListener(LP_CTX_CHANGE_EVENT, onChange);
    });
  }
  async function settleLpCtx() {
    watchLpCtx();
    await Promise.race([
      Promise.resolve(window.theme?.cartAttributesChecked).catch(() => {
      }),
      new Promise((resolve) => setTimeout(resolve, LP_CTX_WAIT_MS))
    ]);
    const attributes = await readCartAttributes();
    if (getLpCtx())
      return getLpCtx();
    if (attributes._fee_province && window.__rslLivePricingContext)
      return waitForLpCtx();
    return "";
  }
  async function resolveLpCtx() {
    const snapshot = getLpCtx();
    if (snapshot)
      return snapshot;
    if (!window.theme?.customer?.isB2B)
      return "";
    if (resolvedLpCtx !== null)
      return resolvedLpCtx;
    resolutionInFlight ||= settleLpCtx().then(
      (ctx) => resolvedLpCtx = ctx,
      () => getLpCtx()
    ).finally(() => {
      resolutionInFlight = null;
    });
    return resolutionInFlight;
  }
  function currencyMatches(config) {
    const active = window.Shopify?.currency?.active;
    if (!active || !config.shopCurrency)
      return true;
    return String(active).toUpperCase() === String(config.shopCurrency).toUpperCase();
  }
  function livePricingMode(config) {
    if (!config)
      return "outage";
    if (!config.displayPrices || !currencyMatches(config))
      return "server-price";
    return "live";
  }
  async function fetchLivePrices(gids, config, ctx) {
    const body = { variants: gids };
    if (config.companyId)
      body.companyId = config.companyId;
    if (config.companyLocationId)
      body.companyLocationId = config.companyLocationId;
    if (ctx)
      body.ctx = ctx;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PRICES_REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(`/apps/${config.proxySubpath || "live-pricing"}/api/live-pricing/prices`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
        signal: controller.signal
      });
      if (!response.ok) {
        const error = new Error(`live pricing responded ${response.status}`);
        error.status = response.status;
        throw error;
      }
      const json = await response.json();
      const prices = /* @__PURE__ */ new Map();
      (json?.prices ?? []).forEach(({ variantId, amount, fees }) => {
        if (variantId != null)
          prices.set(toVariantGid(String(variantId)), { amount, fees });
      });
      const retryableGids = Array.isArray(json?.retryableVariantIds) ? new Set(json.retryableVariantIds.map((variantId) => toVariantGid(String(variantId)))) : null;
      return { prices, retryableGids };
    } finally {
      clearTimeout(timer);
    }
  }
  function livePriceBatches(gids) {
    return chunk(gids, BATCH_SIZE);
  }
  async function fetchLivePriceEntriesOnce(gids, config, ctx) {
    const batches = chunk(gids, BATCH_SIZE);
    const settled = await Promise.allSettled(batches.map((batch) => fetchLivePrices(batch, config, ctx)));
    const entries = /* @__PURE__ */ new Map();
    settled.forEach((outcome, index) => {
      const batchResponse = outcome.status === "fulfilled" ? outcome.value : null;
      const failure = outcome.status === "rejected" ? outcome.reason : null;
      const timedOut = failure?.name === "AbortError";
      const refused = failure?.status >= 400 && failure.status < 500;
      batches[index].forEach((gid) => {
        const entry = batchResponse?.prices.get(gid);
        entries.set(gid, {
          amount: toAmount(entry?.amount),
          fees: Array.isArray(entry?.fees) ? entry.fees : void 0,
          requestFailed: failure != null,
          timedOut,
          refused,
          failedUpstream: batchResponse?.retryableGids?.has(gid) ?? true
        });
      });
    });
    return entries;
  }
  function isWorthRetrying({ amount, refused, timedOut, failedUpstream }) {
    return amount == null && !refused && !timedOut && failedUpstream;
  }
  async function retryUnpricedEntries(entries, config, ctx, { retryOnly } = {}) {
    const unpricedGids = [];
    entries.forEach((entry, gid) => {
      if (!isWorthRetrying(entry))
        return;
      if (retryOnly && !retryOnly.has(gid))
        return;
      unpricedGids.push(gid);
    });
    if (!unpricedGids.length)
      return /* @__PURE__ */ new Map();
    await delay(UNPRICED_RETRY_DELAY_MS);
    return fetchLivePriceEntriesOnce(unpricedGids, config, ctx);
  }

  // app/scripts/utils/formatMoney.js
  var MONEY_TOKENS = {
    amount: [2, ",", "."],
    amount_no_decimals: [0, ",", "."],
    amount_with_comma_separator: [2, ".", ","],
    amount_no_decimals_with_comma_separator: [0, ".", ","],
    amount_with_space_separator: [2, " ", ","],
    amount_no_decimals_with_space_separator: [0, " ", ","],
    amount_with_apostrophe_separator: [2, "'", "."]
  };
  var MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;
  var THOUSANDS_BOUNDARY = /(\d)(?=(\d\d\d)+(?!\d))/g;
  function formatMoney(amount, format) {
    if (!Number.isFinite(amount))
      return "";
    const moneyFormat = format || "${{amount}}";
    const token = MONEY_PLACEHOLDER.exec(moneyFormat)?.[1];
    const [precision, thousands, decimalMark] = MONEY_TOKENS[token] ?? MONEY_TOKENS.amount;
    const [whole, fraction] = amount.toFixed(precision).split(".");
    const grouped = whole.replace(THOUSANDS_BOUNDARY, `$1${thousands}`);
    return moneyFormat.replace(MONEY_PLACEHOLDER, fraction ? grouped + decimalMark + fraction : grouped);
  }

  // app/scripts/modules/liveFeeNote.js
  function numericId(gid) {
    return String(gid).split("/").pop();
  }
  function toCents(amount) {
    const value = toAmount(amount);
    return value == null ? null : Math.round(value * 100);
  }
  function noteText(note, ecoCents, depositCents, moneyFormat) {
    const eco = formatMoney(ecoCents / 100, moneyFormat);
    const deposit = formatMoney(depositCents / 100, moneyFormat);
    if (ecoCents > 0 && depositCents > 0) {
      return (note.dataset.liveFeeBoth || "").replace("__ECO__", eco).replace("__DEPOSIT__", deposit);
    }
    if (depositCents > 0)
      return (note.dataset.liveFeeDeposit || "").replace("__DEPOSIT__", deposit);
    if (ecoCents > 0)
      return (note.dataset.liveFeeEco || "").replace("__ECO__", eco);
    return "";
  }
  function applyFeeNotes(gid, fees, moneyFormat) {
    if (!Array.isArray(fees))
      return;
    document.querySelectorAll(`[data-live-fee-variant="${numericId(gid)}"]`).forEach((note) => {
      const depositIds = new Set((note.dataset.liveFeeDeposits || "").split(",").filter(Boolean));
      const levyIds = new Set((note.dataset.liveFeeLevies || "").split(",").filter(Boolean));
      let ecoCents = 0;
      let depositCents = 0;
      let classified = true;
      for (const fee of fees) {
        const cents = toCents(fee?.amount);
        if (cents == null)
          return;
        const id = fee?.variantId == null ? "" : numericId(fee.variantId);
        if (depositIds.has(id))
          depositCents += cents;
        else if (levyIds.has(id))
          ecoCents += cents;
        else
          classified = false;
      }
      const text = classified ? noteText(note, ecoCents, depositCents, moneyFormat) : "";
      note.textContent = text;
      note.hidden = text === "";
      note.classList.toggle("hidden", text === "");
    });
  }

  // app/scripts/modules/livePriceDisplay.js
  var PENDING_CLASS = "is-live-price-pending";
  var CACHE_TTL_MS = 10 * 60 * 1e3;
  var CACHE_STORAGE_KEY = "theme.livePriceCache";
  var CACHE_MAX_ENTRIES = 1e3;
  var STATE_ROOT_SELECTOR = "card-product, card-product-extended, .pdp-product-form, .replacement-price";
  var cache = /* @__PURE__ */ new Map();
  var cacheLoaded = false;
  var inFlight = null;
  var queuedScopes = /* @__PURE__ */ new Set();
  var scanIdByElement = /* @__PURE__ */ new WeakMap();
  var feeNotesScanIdByVariant = /* @__PURE__ */ new Map();
  var skeletonHosts = /* @__PURE__ */ new WeakSet();
  var lastScanId = 0;
  var store = sessionStorage(CACHE_STORAGE_KEY);
  function readStoredCache() {
    const stored = store.get();
    if (!stored || typeof stored !== "object" || Array.isArray(stored))
      return;
    const now = Date.now();
    Object.entries(stored).forEach(([key, entry]) => {
      if (entry && typeof entry === "object" && entry.exp > now)
        cache.set(key, entry);
    });
  }
  function writeStoredCache() {
    try {
      store.set(Object.fromEntries(cache));
    } catch (error) {
    }
  }
  function evictExcess() {
    while (cache.size > CACHE_MAX_ENTRIES) {
      cache.delete(cache.keys().next().value);
    }
  }
  function pruneCache() {
    const now = Date.now();
    cache.forEach((entry, key) => {
      if (entry.exp <= now)
        cache.delete(key);
    });
    evictExcess();
  }
  function collectVariantElements(roots) {
    const byGid = /* @__PURE__ */ new Map();
    roots.forEach((root) => {
      const found = Array.from(root.querySelectorAll("[data-live-price-variant]"));
      if (root.matches?.("[data-live-price-variant]"))
        found.unshift(root);
      found.forEach((el) => {
        const rawId = el.dataset.livePriceVariant;
        if (!rawId)
          return;
        const gid = toVariantGid(rawId);
        if (!byGid.has(gid))
          byGid.set(gid, []);
        byGid.get(gid).push(el);
      });
    });
    return byGid;
  }
  function getContextKey(config, ctx) {
    return [config.customerId ?? "", config.companyId ?? "", config.companyLocationId ?? "", ctx].join("|");
  }
  function cacheKey(gid, contextKey) {
    return `${gid}::${contextKey}`;
  }
  function setPurchasable(root, purchasable) {
    root?.querySelectorAll("[data-live-price-gate]").forEach((control) => {
      control.disabled = !purchasable;
    });
  }
  function setPriceState(root, amount) {
    if (!root)
      return;
    setPurchasable(root, amount != null);
    if (typeof root.setLivePrice === "function")
      root.setLivePrice(amount);
    else
      root.classList.toggle("price-unavailable", amount == null);
  }
  function applyPrice(elements, price, config) {
    const amount = toAmount(price);
    elements.forEach((container) => {
      setPriceState(container.closest(STATE_ROOT_SELECTOR), amount);
      if (amount == null)
        return;
      const formatted = formatMoney(amount, config.moneyFormat);
      if (!formatted)
        return;
      const net = container.querySelector("[data-live-price-net]");
      const netLabel = container.querySelector("[data-live-price-net-label]");
      const list = container.querySelector("[data-live-price-list]");
      const compare = container.querySelector("[data-live-price-compare]");
      const listAmount = list ? Number(list.dataset.livePriceList) / 100 : NaN;
      const showList = Number.isFinite(listAmount) && amount < listAmount;
      if (net)
        net.textContent = formatted;
      if (list)
        list.hidden = !showList;
      if (netLabel)
        netLabel.hidden = !showList;
      if (compare)
        compare.hidden = true;
    });
  }
  function clearPending(elements) {
    elements.forEach((el) => {
      el.classList.remove(PENDING_CLASS);
      el.closest(STATE_ROOT_SELECTOR)?.classList.remove(PENDING_CLASS);
    });
  }
  function restorePending(elements) {
    elements.forEach((el) => {
      const root = el.closest(STATE_ROOT_SELECTOR);
      [el, root].forEach((host) => {
        if (host && skeletonHosts.has(host))
          host.classList.add(PENDING_CLASS);
      });
      setPurchasable(root, false);
    });
  }
  async function runScan(roots) {
    const byGid = collectVariantElements(roots);
    if (!byGid.size)
      return;
    const scanId = ++lastScanId;
    byGid.forEach((elements) => {
      elements.forEach((el) => {
        scanIdByElement.set(el, scanId);
        [el, el.closest(STATE_ROOT_SELECTOR)].forEach((host) => {
          if (host?.classList.contains(PENDING_CLASS))
            skeletonHosts.add(host);
        });
      });
    });
    let retryingGids;
    try {
      retryingGids = await applyLivePrices(byGid, scanId);
    } finally {
      byGid.forEach((elements, gid) => {
        if (!retryingGids?.has(gid))
          clearPending(elements);
      });
    }
  }
  function applyFeeNotesForScan(gid, fees, config, scanId) {
    if (scanId < (feeNotesScanIdByVariant.get(gid) ?? 0))
      return;
    feeNotesScanIdByVariant.set(gid, scanId);
    applyFeeNotes(gid, fees, config.moneyFormat);
  }
  function applyEntries(entries, byGid, config, contextKey, scanId) {
    let cached = false;
    entries.forEach(({ amount, fees, failedUpstream }, gid) => {
      if (amount != null || failedUpstream === false) {
        const key = cacheKey(gid, contextKey);
        cache.delete(key);
        cache.set(key, { price: amount, fees, exp: Date.now() + CACHE_TTL_MS });
        cached = true;
      }
      applyPrice(byGid.get(gid), amount, config);
      if (amount != null)
        applyFeeNotesForScan(gid, fees, config, scanId);
    });
    if (cached) {
      evictExcess();
      writeStoredCache();
    }
  }
  function settleUnpricedInBackground(entries, byGid, config, ctx, contextKey, scanId) {
    const unpricedGids = Array.from(entries.keys()).filter((gid) => entries.get(gid).amount == null);
    const ownElements = (gid) => byGid.get(gid).filter((el) => el.isConnected && scanIdByElement.get(el) === scanId);
    retryUnpricedEntries(entries, config, ctx).catch(() => /* @__PURE__ */ new Map()).then((retried) => {
      const owned = new Map(
        unpricedGids.map((gid) => [gid, ownElements(gid)]).filter(([, elements]) => elements.length)
      );
      const settled = new Map(Array.from(owned.keys(), (gid) => [gid, retried.get(gid) ?? { amount: null }]));
      applyEntries(settled, owned, config, contextKey, scanId);
    }).finally(() => unpricedGids.forEach((gid) => clearPending(ownElements(gid)))).catch(() => {
    });
    return new Set(unpricedGids);
  }
  async function applyLivePrices(byGid, scanId) {
    const config = getLivePricingConfig();
    const mode = livePricingMode(config);
    if (mode === "outage") {
      byGid.forEach((elements) => applyPrice(elements, null, null));
      return;
    }
    if (mode === "server-price") {
      byGid.forEach((elements) => elements.forEach((el) => setPurchasable(el.closest(STATE_ROOT_SELECTOR), true)));
      return;
    }
    if (!cacheLoaded) {
      cacheLoaded = true;
      readStoredCache();
    }
    pruneCache();
    const ctx = await resolveLpCtx();
    const contextKey = getContextKey(config, ctx);
    const pending = [];
    byGid.forEach((elements, gid) => {
      const entry = cache.get(cacheKey(gid, contextKey));
      if (entry) {
        applyPrice(elements, entry.price, config);
        if (entry.price != null)
          applyFeeNotesForScan(gid, entry.fees, config, scanId);
        clearPending(elements);
        return;
      }
      pending.push(gid);
    });
    if (!pending.length)
      return;
    const retryingByBatch = await Promise.all(
      livePriceBatches(pending).map((batch) => applyBatch(batch, byGid, config, ctx, contextKey, scanId))
    );
    return new Set(retryingByBatch.flatMap((retryingGids) => Array.from(retryingGids ?? [])));
  }
  async function applyBatch(gids, byGid, config, ctx, contextKey, scanId) {
    const entries = await fetchLivePriceEntriesOnce(gids, config, ctx);
    const retryingEntries = /* @__PURE__ */ new Map();
    const settledEntries = /* @__PURE__ */ new Map();
    entries.forEach((entry, gid) => {
      (isWorthRetrying(entry) ? retryingEntries : settledEntries).set(gid, entry);
    });
    applyEntries(settledEntries, byGid, config, contextKey, scanId);
    settledEntries.forEach((_entry, gid) => clearPending(byGid.get(gid)));
    if (!retryingEntries.size)
      return;
    retryingEntries.forEach((_entry, gid) => restorePending(byGid.get(gid)));
    return settleUnpricedInBackground(retryingEntries, byGid, config, ctx, contextKey, scanId);
  }
  function drainQueue() {
    inFlight = null;
    const roots = Array.from(queuedScopes).filter((root) => root === document || root.isConnected);
    queuedScopes.clear();
    if (!roots.length)
      return;
    return startScan(roots);
  }
  function startScan(roots) {
    const run = runScan(roots).catch(() => {
    }).then(drainQueue);
    inFlight = run;
    return run;
  }
  function refresh(scope = document) {
    const roots = Array.isArray(scope) ? scope : [scope];
    if (inFlight) {
      roots.forEach((root) => queuedScopes.add(root));
      return inFlight;
    }
    return startScan(roots);
  }

  // app/scripts/live-price-display.js
  window.themeLivePrice = { refresh };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => refresh(document), { once: true });
  } else {
    refresh(document);
  }
  window.addEventListener(LP_CTX_CHANGE_EVENT, () => refresh(document));
  watchLpCtx();
})();
